import { resolveDrillzMetrics } from '../drillzCampaignProgress';
import { resolveSourceProjectName } from '../drillingCampaignHelpers';

const DEFAULT_PROGRAM_TARGET_M = 50000;

function parseTargetDepthMeters(raw) {
  if (raw == null || raw === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function resolveTargetDepthM(projectSources, selectedProjectId) {
  if (!projectSources?.length) return DEFAULT_PROGRAM_TARGET_M;
  if (selectedProjectId === 'all') {
    let sum = 0;
    let any = false;
    for (const p of projectSources) {
      const v = parseTargetDepthMeters(p.target);
      if (v != null) {
        sum += v;
        any = true;
      }
    }
    return any ? sum : DEFAULT_PROGRAM_TARGET_M;
  }
  const proj = projectSources.find(
    (p) => String(p.project_id) === String(selectedProjectId)
  );
  const v = proj ? parseTargetDepthMeters(proj.target) : null;
  return v != null ? v : DEFAULT_PROGRAM_TARGET_M;
}

export function filterCampaignHoles(dashboardData, selectedProjectId) {
  if (!Array.isArray(dashboardData)) return [];
  if (selectedProjectId === 'all') return dashboardData;
  const anyKeyed = dashboardData.some(
    (r) => r.project_id != null && String(r.project_id) !== ''
  );
  if (!anyKeyed) return dashboardData;
  return dashboardData.filter(
    (r) => String(r.project_id) === String(selectedProjectId)
  );
}

function pct(numerator, denominator) {
  const num = Number(numerator) || 0;
  const den = Number(denominator) || 0;
  if (den <= 0) return 0;
  return Math.min((num / den) * 100, 100);
}

/**
 * Build drilling program metrics for the daily report PDF (mirrors Drilling Dashboard header).
 */
export function buildDrillingMetricsForReport({
  dashboardData,
  projectSources,
  selectedProjectId,
  sampleProgressBundle,
  drillzCampaignRows,
}) {
  const campaignBaseData = filterCampaignHoles(dashboardData, selectedProjectId);
  const activeFilteredHoles = campaignBaseData.filter(
    (r) =>
      r.hole_status === 'Complete' ||
      r.hole_status === 'In Progress' ||
      r.hole_status === 'Lost'
  );

  const totalHoles = activeFilteredHoles.length;
  const completedHoles = campaignBaseData.filter(
    (r) => r.hole_status === 'Complete'
  ).length;
  const inProgressHoles = campaignBaseData.filter(
    (r) => r.hole_status === 'In Progress'
  ).length;

  const totalDrilledDepth = activeFilteredHoles.reduce(
    (sum, r) => sum + (parseFloat(r.final_depth) || parseFloat(r.current_depth) || 0),
    0
  );
  const totalLoggedDepth = activeFilteredHoles.reduce(
    (sum, r) => sum + (parseFloat(r.logged_depth_m) || 0),
    0
  );

  const avgDrilledPct =
    activeFilteredHoles.reduce(
      (sum, r) => sum + (parseFloat(r.drilled_pct) || 0),
      0
    ) / totalHoles || 0;
  const avgLoggedPct =
    activeFilteredHoles.reduce(
      (sum, r) => sum + (parseFloat(r.logged_pct) || 0),
      0
    ) / totalHoles || 0;

  const targetDepthM = resolveTargetDepthM(projectSources, selectedProjectId);

  const drillzMetrics = resolveDrillzMetrics({
    drillzRows: drillzCampaignRows,
    projectSources,
    selectedProjectId,
  });
  const usingDrillzProgress = drillzMetrics.source === 'drillz';

  const drilledDepthM = usingDrillzProgress
    ? drillzMetrics.metersDrilled
    : totalDrilledDepth;
  const loggedDepthM = usingDrillzProgress
    ? drillzMetrics.metersLogged
    : totalLoggedDepth;
  const cutDepthM = usingDrillzProgress ? drillzMetrics.metersCut : 0;

  const drilledPct = pct(drilledDepthM, targetDepthM);
  const loggedPct = pct(loggedDepthM, targetDepthM);
  const cutPct = pct(cutDepthM, targetDepthM);

  const campaignLabel =
    selectedProjectId === 'all'
      ? 'All projects'
      : resolveSourceProjectName(
          selectedProjectId,
          sampleProgressBundle?.projects
        ) || String(selectedProjectId);

  return {
    totalHoles,
    completedHoles,
    inProgressHoles,
    avgDrilledPct,
    avgLoggedPct,
    targetDepthM,
    drilledDepthM,
    loggedDepthM,
    cutDepthM,
    drilledPct,
    loggedPct,
    cutPct,
    usingDrillzProgress,
    campaignLabel,
  };
}
